import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function isSchemaError(error) {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState(null);

  const getClientForRole = (role) => {
    return role === 'admin' ? supabaseAdmin : supabase;
  };

  const fetchUserProfile = async (userId, role) => {
    try {
      const client = getClientForRole(role);
      const { data, error } = await client?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          setUserProfile(null);
          return null;
        }
      }
      setUserProfile(data);
      return data;
    } catch (error) {
      console.log('Profile fetch error:', error?.message);
      return null;
    }
  };

  useEffect(() => {
    // ── 1. Get initial session on mount ──────────────────────────────────
    const initAuth = async () => {
      try {
        // Check admin session first
        const { data: { user: adminUser } } = await supabaseAdmin?.auth?.getUser();
        if (adminUser) {
          setUser(adminUser);
          setCurrentRole('admin');
          await fetchUserProfile(adminUser?.id, 'admin');
          setLoading(false);
          return;
        }

        // Check customer session
        const { data: { user: customerUser } } = await supabase?.auth?.getUser();
        if (customerUser) {
          setUser(customerUser);
          setCurrentRole('customer');
          fetchUserProfile(customerUser?.id, 'customer').catch(() => {});
        }
      } catch (error) {
        console.log('Auth check error:', error?.message);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // ── 2. Listen for auth state changes (OTP login, sign out, etc.) ─────
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // console.log('Auth event:', event, session?.user?.email);

        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          setCurrentRole('customer');
          // fetchUserProfile may fail if user_profiles row doesn't exist yet
          // so we set loading false first to unblock the route
          setLoading(false);
          fetchUserProfile(session.user.id, 'customer').catch(() => {});
        }

        if (event === 'SIGNED_OUT') {
          setUser(null);
          setUserProfile(null);
          setCurrentRole(null);
        }

        if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user);
        }
      }
    );

    // Cleanup listener on unmount
    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email, password, role = 'customer') => {
    try {
      const client = getClientForRole(role);
      const { data, error } = await client?.auth?.signInWithPassword({ email, password });
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }
      setUser(data?.user);
      setCurrentRole(role);
      const profile = await fetchUserProfile(data?.user?.id, role);
      return { ...data, userProfile: profile };
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: { data: metadata }
      });
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const signOut = async (role = currentRole) => {
    try {
      const client = getClientForRole(role);
      const { error } = await client?.auth?.signOut();
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
      }
      setUser(null);
      setUserProfile(null);
      setCurrentRole(null);
    } catch (error) {
      console.log('Sign out error:', error?.message);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    currentRole,
    signIn,
    signUp,
    signOut,
    isAdmin: userProfile?.role === 'admin',
    getClientForRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};